import { useMemo, useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { api } from '../../shared/api';
import { fmtTime } from '../../shared/dates';

interface AvailabilitySlot {
  day: string;
  startMin: number;
  endMin: number;
}

const DAYS = ['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN'] as const;

export function ProviderDetailPage() {
  const { id } = useParams<{ id: string }>();
  const queryClient = useQueryClient();

  const { data: provider } = useQuery({
    queryKey: ['provider', id],
    queryFn: () => api.get(`/providers/${id}`).then((r) => r.data),
    enabled: !!id,
  });

  const [draft, setDraft] = useState<AvailabilitySlot[]>([]);

  useEffect(() => {
    if (provider) setDraft(provider.availability ?? []);
  }, [provider]);

  const save = useMutation({
    mutationFn: () =>
      api.put(`/providers/${id}/availability`, draft).then((r) => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['provider', id] });
      queryClient.invalidateQueries({ queryKey: ['providers'] });
      toast.success('Availability saved');
    },
  });

  const grouped = useMemo(() => {
    const g: Record<string, AvailabilitySlot[]> = {};
    for (const s of draft) {
      (g[s.day] ??= []).push(s);
    }
    return g;
  }, [draft]);

  function addSlot(day: string) {
    setDraft((d) => [...d, { day, startMin: 9 * 60, endMin: 17 * 60 }]);
  }
  function removeSlot(idx: number) {
    setDraft((d) => d.filter((_, i) => i !== idx));
  }
  function updateSlot(idx: number, patch: Partial<AvailabilitySlot>) {
    setDraft((d) => d.map((s, i) => (i === idx ? { ...s, ...patch } : s)));
  }

  if (!provider) return <div>Loading...</div>;

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold">
            {provider.firstName} {provider.lastName}
          </h1>
          <p className="text-sm text-slate-500">
            {provider.role} · {provider.maxDirectHoursPerWeek}h/wk cap ·{' '}
            <span className={provider.active ? 'pill-success' : 'pill-muted'}>
              {provider.active ? 'Active' : 'Inactive'}
            </span>
          </p>
        </div>
        <div className="flex gap-2">
          <Link className="btn-secondary" to={`/print/provider/${id}`} target="_blank">
            Print week
          </Link>
          <Link className="btn-secondary" to="/providers">← Back</Link>
        </div>
      </header>

      <section className="card p-4">
        <div className="flex items-center justify-between">
          <h2 className="text-base font-semibold">Weekly availability</h2>
          <button
            className="btn-primary"
            onClick={() => save.mutate()}
            disabled={save.isPending}
          >
            {save.isPending ? 'Saving…' : 'Save changes'}
          </button>
        </div>
        <p className="mt-1 text-xs text-slate-500">
          Time pairs are minutes from midnight (5-min granularity). Format HH:MM accepted.
        </p>

        <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-7">
          {DAYS.map((day) => (
            <div key={day} className="rounded-lg border border-slate-200 p-3">
              <div className="mb-2 flex items-center justify-between">
                <span className="text-sm font-semibold text-slate-700">{day}</span>
                <button className="text-xs text-brand-600 hover:underline" onClick={() => addSlot(day)}>
                  + slot
                </button>
              </div>
              {(grouped[day] ?? []).length === 0 && (
                <p className="text-xs text-slate-400">Unavailable</p>
              )}
              {(grouped[day] ?? []).map((s, idx) => {
                const realIdx = draft.indexOf(s);
                return (
                  <div key={realIdx} className="mb-2 flex items-center gap-1">
                    <TimeInput
                      value={s.startMin}
                      onChange={(v) => updateSlot(realIdx, { startMin: v })}
                    />
                    <span className="text-slate-400">–</span>
                    <TimeInput
                      value={s.endMin}
                      onChange={(v) => updateSlot(realIdx, { endMin: v })}
                    />
                    <button
                      className="ml-auto text-xs text-rose-600 hover:underline"
                      onClick={() => removeSlot(realIdx)}
                    >
                      ×
                    </button>
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}

function TimeInput({ value, onChange }: { value: number; onChange: (v: number) => void }) {
  const [text, setText] = useState(toHHMM(value));
  useEffect(() => setText(toHHMM(value)), [value]);
  function commit() {
    const parsed = fromHHMM(text);
    if (parsed != null) onChange(parsed);
    else setText(toHHMM(value));
  }
  return (
    <input
      className="input w-20 px-1 py-1 text-center"
      value={text}
      onChange={(e) => setText(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => e.key === 'Enter' && commit()}
      title={`Currently ${fmtTime(value)}`}
    />
  );
}

function toHHMM(min: number): string {
  const h = Math.floor(min / 60);
  const m = min % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}
function fromHHMM(v: string): number | null {
  const m = /^(\d{1,2}):(\d{2})$/.exec(v.trim());
  if (!m) return null;
  const h = Number(m[1]);
  const min = Number(m[2]);
  if (h > 23 || min > 59) return null;
  return h * 60 + min;
}
